import {CuePoint} from '@playkit-js/common';
import {CaptionListLoader} from './caption-list-loader';
import {AadCaptionLoader} from './caption-loader';
import {KalturaCaption} from './kaltura-caption';
import {AAD_CUE_POINT_METADATA_TYPE_VALUE, AADCuePointMetadata, CaptionAssetUsage} from './parse-webvtt-service';

interface CaptionAssetResponse {
  id: string;
  languageCode: string;
  // Usage can be returned as number or string from the API
  usage: string | number;
}

class CaptionAssetService {
  private player: KalturaPlayerTypes.Player;
  private logger: KalturaPlayerTypes.Logger;

  constructor(player: KalturaPlayerTypes.Player, logger: KalturaPlayerTypes.Logger) {
    this.player = player;
    this.logger = logger;
  }

  public async fetchAllAadCaptions(entryId: string): Promise<any[]> {
    const ks = this.player.config?.session?.ks;
    const captionListResponsesMap = await this.player.provider.doRequest([{loader: CaptionListLoader, params: {entryId}}], ks);
    const captionAssets = captionListResponsesMap.get(CaptionListLoader.id);

    if (!Array.isArray(captionAssets.response.captionAssets) || !captionAssets.response.captionAssets.length) {
      return [];
    }

    const captions = captionAssets.response.captionAssets
      .filter((captionAsset: CaptionAssetResponse) => {
        // Convert usage to string for comparison since API may return number or string
        return String(captionAsset.usage) === CaptionAssetUsage.AAD;
      })
      .map((captionAsset: CaptionAssetResponse) => {
        const {id, languageCode} = captionAsset;
        return {
          id,
          languageCode
        };
      });

    return Promise.all(
      captions.map(async (captionAsset: any) => {
        const captions = await this.fetchAadCaptions(captionAsset.id, captionAsset.languageCode);
        return captions.map(c => ({...c, id: `${c.id}_${captionAsset.languageCode}`}));
      })
    ).then(allCaptions => allCaptions.flat());
  }

  public async fetchAadCaptions(captionAssetId: string, language: string): Promise<Array<CuePoint<AADCuePointMetadata>>> {
    const ks = this.player.config?.session?.ks;

    const captionResponsesMap = await this.player.provider.doRequest([{loader: AadCaptionLoader, params: {captionAssetId}}], ks);
    const captionData = captionResponsesMap.get(AadCaptionLoader.id);
    if (!Array.isArray(captionData.response.captions) || !captionData.response.captions.length) {
      throw new Error("No AAD caption asset doesn't contain any captions");
    }

    const captions = captionData.response.captions.map((caption: KalturaCaption, index: number) => {
      return {
        id: index.toString(),
        startTime: caption.startTime / 1000,
        endTime: caption.endTime / 1000,
        type: '',
        metadata: {
          type: AAD_CUE_POINT_METADATA_TYPE_VALUE,
          identifier: index.toString(),
          text: caption.content.reduce((acc, cur) => {
            return `${acc}${cur.text}`;
          }, ''),
          styles: '',
          language
        }
      };
    });
    return Promise.resolve(captions);
  }
}

export {CaptionAssetService};
